import React from "react";
import _ from "lodash";
import { formatDateWithName } from "services/MomentUtils";
import { isCollectionNullOrEmpty } from "services/UtilityService";
import {
  ALL_FAX_TAG,
  UFL_URL_FILTER_CONFIG,
  UNPROCESSED_FAX_PROGRAM_TYPE,
  UNKNOWN,
} from "lookup/UnprocessedFaxListConstantsV2";
import { renderDateCell } from "commons/utils";
import { createListUrlFilterUtils } from "commons/utils/queryParamUtils";

export const renderLastModifiedDateCol = (date, name) =>
  date ? renderDateCell(formatDateWithName(date, name)) : <span />;

export const getFaxProgramType = (isSwitchProgram) => {
  if (isSwitchProgram) {
    return UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
  } else {
    return UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH;
  }
};

export const getFaxTypeForRoute = (programType) => {
  switch (programType) {
    case UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH:
      return "Switch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH:
      return "NonSwitch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN:
    default:
      return "Unknown";
  }
};

const urlFilterUtils = createListUrlFilterUtils({
  filterConfig: UFL_URL_FILTER_CONFIG,
  staticParamKeys: ["faxProgramCode"],
  markBuiltFiltersAsDefault: true,
  shouldSkipFilterWhenBuilding: (config, { isSwitchFax, isAssociatedSwitchTrue }) =>
    config.skipWhenSwitchAssociated && isSwitchFax && isAssociatedSwitchTrue,
  appendBuiltFilters: (newFilters, { faxProgramType }) => {
    if (faxProgramType) {
      newFilters.push({
        id: "faxProgramType",
        value: faxProgramType,
        isdefault: true,
      });
    }
    return newFilters;
  },
});

export const {
  getUrlFilterValues,
  getFilterValueFromUrl,
  buildSearchFromPagination,
} = urlFilterUtils;

export const buildPrimaryFilters = (
  currentFilters,
  urlFilterValues,
  faxProgramType
) => {
  const associatedSwitchFilter = currentFilters.find(
    (f) => f.id === "associatedSwitchId"
  );
  const isAssociatedSwitchTrue =
    associatedSwitchFilter &&
    (associatedSwitchFilter.value === true ||
      associatedSwitchFilter.value === "true");
  const isSwitchFax = faxProgramType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;

  return urlFilterUtils.buildPrimaryFilters(currentFilters, urlFilterValues, {
    isSwitchFax,
    isAssociatedSwitchTrue,
    faxProgramType,
  });
};

export const normalizeMultiSelectIdsFilterValue = (value, options) => {
  if (!Array.isArray(value) || !Array.isArray(options) || options.length === 0)
    return value;
  if (value.length === 0) return value;
  const containsObjectValues = value.some(
    (entry) => entry && typeof entry === "object"
  );
  if (containsObjectValues) return value;

  return value
    .map((entry) => {
      const id = String(entry);
      return options.find((option) => String(option.id) === id) || { id, name: id };
    })
    .filter(Boolean);
};

const tagEntryToId = (entry) =>
  typeof entry === "object" && entry !== null ? entry.id : entry;

const customerTagsPayloadFromTableTagIds = (tagIds, programCode) => {
  const ids = Array.isArray(tagIds) ? tagIds.map((id) => String(id)).filter(Boolean) : [];
  const withoutProgram = programCode
    ? ids.filter((id) => id !== String(programCode))
    : ids;
  if (withoutProgram.some((id) => id === ALL_FAX_TAG.id)) return ["ALL"];
  return Array.from(new Set(withoutProgram));
};

/** @param {string} [faxProgramCode] From URL — strips program id from tag ids for `customerTags`. */
export const filterModification = (filtered, faxProgramCode = "") => {
  let filteredDeepCopy = _.cloneDeep(filtered);

  if (filteredDeepCopy && filteredDeepCopy.length > 0) {
    const showMyRecentsFilter = filteredDeepCopy.find(
      (f) => f.id === "showMyRecentsOnly"
    );

    if (showMyRecentsFilter && showMyRecentsFilter.value === true) {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) filteredDeepCopy.splice(idx, 1);
    } else {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) {
        const filter = filteredDeepCopy[idx];
        if (filter.value.length > 0) {
          filteredDeepCopy[idx].value = filter.value
            .map((u) => (typeof u === "object" && u !== null ? u.id : u))
            .filter(Boolean);
        } else filteredDeepCopy.splice(idx, 1);
      }
    }

    const tagsIndex = filteredDeepCopy.findIndex((f) => f.id === "tags");
    const hadTagsFilter = tagsIndex > -1;
    let tagIds = [];
    if (hadTagsFilter) {
      const filter = filteredDeepCopy[tagsIndex];
      tagIds = (filter.value || []).map(tagEntryToId).filter(Boolean);
      filteredDeepCopy.splice(tagsIndex, 1);
    }

    // Treat the URL `faxProgramCode` (including `Unknown`) as the faxProgramTag.
    // This ensures we never accidentally send it as a customerTag.
    const programCode = faxProgramCode ? String(faxProgramCode) : "";

    const customerTagsPayload = hadTagsFilter
      ? customerTagsPayloadFromTableTagIds(tagIds, programCode)
      : [];

    if (hadTagsFilter) {
      if (programCode) {
        filteredDeepCopy.push({
          id: "faxProgramTag",
          value: programCode,
        });
      }
      filteredDeepCopy.push({
        id: "customerTags",
        value: customerTagsPayload,
      });
    }

    filteredDeepCopy = filteredDeepCopy.filter(
      (f) =>
        f.id === "customerTags" ||
        !(f.value instanceof Array) ||
        (f.value instanceof Array && !isCollectionNullOrEmpty(f.value))
    );
  }

  return filteredDeepCopy;
};

export const sortedModification = (sorted) => {
  const sortedCopy = _.cloneDeep(sorted);
  if (sortedCopy && sortedCopy.length > 0) {
    const idx = sortedCopy.findIndex((f) => f.id === "recentlyViewedUsers");
    if (idx > -1) sortedCopy[idx].id = "lastViewedBy";
  }
  return sortedCopy;
};
